/**
 * @file history.ts
 * @description Action-based undo/redo history manager.
 * Migrated from history_manager.js.
 */

import { g, layers } from './globals';
import type { IHistoryAction } from './types';
import type { LayerClass } from './globals';
import { EventBus } from '@hcie/shared';

// ─── History Manager ───────────────────────────────────────

export class HistoryManager {
  private actions: IHistoryAction[] = [];
  private currentIndex = -1;
  private readonly maxSteps: number;

  constructor(maxSteps = 200) {
    this.maxSteps = maxSteps;
  }

  push(action: IHistoryAction): void {
    // Branching: discard future actions when a new one is recorded
    if (this.currentIndex < this.actions.length - 1) {
      this.actions = this.actions.slice(0, this.currentIndex + 1);
    }
    this.actions.push(action);
    this.currentIndex++;

    // Mark document as modified (#2069)
    try {
        const activeDoc = g.documents && g.documents[g.activeDocumentIndex] as any;
        if (activeDoc) {
          activeDoc.modified = true;
          EventBus.emit('DOCUMENT_TABS_CHANGED'); // Use the event bus directly
        }
    } catch (e) {
        // Global state not ready
    }

    if (this.actions.length > this.maxSteps) {
      this.actions.shift();
      this.currentIndex--;
    }
    this.notifyUI();
  }

  undo(): void {
    if (this.currentIndex < 0) return;
    const action = this.actions[this.currentIndex];
    console.log(`Undo: ${action.description}`);
    action.undo();
    this.currentIndex--;
    this.notifyUI();
  }

  redo(): void {
    if (this.currentIndex >= this.actions.length - 1) return;
    this.currentIndex++;
    const action = this.actions[this.currentIndex];
    console.log(`Redo: ${action.description}`);
    action.redo();
    this.notifyUI();
  }

  jumpTo(targetIndex: number): void {
    if (targetIndex === this.currentIndex) return;
    if (targetIndex < this.currentIndex) {
      while (this.currentIndex > targetIndex) this.undo();
    } else {
      while (this.currentIndex < targetIndex) this.redo();
    }
  }

  clear(): void {
    this.actions = [];
    this.currentIndex = -1;
    this.notifyUI();
  }

  get canUndo(): boolean { return this.currentIndex >= 0; }
  get canRedo(): boolean { return this.currentIndex < this.actions.length - 1; }
  get currentActions(): IHistoryAction[] { return [...this.actions]; }
  get currentIdx(): number { return this.currentIndex; }

  private notifyUI(): void {
    EventBus.emit('HISTORY_CHANGED', {
      actions: this.actions.map((a, i) => ({
        description: a.description,
        active: i === this.currentIndex,
        ghosted: i > this.currentIndex
      })),
      currentIndex: this.currentIndex
    });
  }
}

// ─── Action Implementations ────────────────────────────────

export class DrawAction implements IHistoryAction {
  readonly description: string;
  private readonly layerIndex: number;
  private readonly previousImageData: ImageData;
  private readonly newImageData: ImageData;

  constructor(layerIndex: number, previousImageData: ImageData, newImageData: ImageData, description?: string) {
    this.description = description || 'Draw';
    this.layerIndex = layerIndex;
    this.previousImageData = previousImageData;
    this.newImageData = newImageData;
  }

  undo(): void {
    const layer = layers[this.layerIndex];
    if (layer) {
      (layer.ctx as CanvasRenderingContext2D).putImageData(this.previousImageData, 0, 0);
      EventBus.emit('RENDER_LAYERS');
      EventBus.emit('UPDATE_LAYER_PANEL');
    }
  }

  redo(): void {
    const layer = layers[this.layerIndex];
    if (layer) {
      (layer.ctx as CanvasRenderingContext2D).putImageData(this.newImageData, 0, 0);
      EventBus.emit('RENDER_LAYERS');
      EventBus.emit('UPDATE_LAYER_PANEL');
    }
  }
}

export class LayerAddAction implements IHistoryAction {
  readonly description = 'Add Layer';
  private readonly layerIndex: number;
  private readonly layerData: LayerClass;

  constructor(layerIndex: number, layerData: LayerClass) {
    this.layerIndex = layerIndex;
    this.layerData = layerData;
  }

  undo(): void {
    layers.splice(this.layerIndex, 1);
    if (g.activeLayerIndex >= layers.length) {
      g.activeLayerIndex = Math.max(0, layers.length - 1);
    }
    EventBus.emit('RENDER_LAYERS');
    EventBus.emit('UPDATE_LAYER_PANEL');
  }

  redo(): void {
    layers.splice(this.layerIndex, 0, this.layerData);
    g.activeLayerIndex = this.layerIndex;
    EventBus.emit('RENDER_LAYERS');
    EventBus.emit('UPDATE_LAYER_PANEL');
  }
}

export class LayerDeleteAction implements IHistoryAction {
  readonly description = 'Delete Layer';
  private readonly layerIndex: number;
  private readonly layerData: LayerClass;

  constructor(layerIndex: number, layerData: LayerClass) {
    this.layerIndex = layerIndex;
    this.layerData = layerData;
  }

  undo(): void {
    layers.splice(this.layerIndex, 0, this.layerData);
    g.activeLayerIndex = this.layerIndex;
    EventBus.emit('RENDER_LAYERS');
    EventBus.emit('UPDATE_LAYER_PANEL');
  }

  redo(): void {
    layers.splice(this.layerIndex, 1);
    if (g.activeLayerIndex >= layers.length) {
      g.activeLayerIndex = Math.max(0, layers.length - 1);
    }
    EventBus.emit('RENDER_LAYERS');
    EventBus.emit('UPDATE_LAYER_PANEL');
  }
}

export class LayerMoveAction implements IHistoryAction {
  readonly description = 'Move Layer';
  private readonly fromIndex: number;
  private readonly toIndex: number;

  constructor(fromIndex: number, toIndex: number) {
    this.fromIndex = fromIndex;
    this.toIndex = toIndex;
  }

  undo(): void { EventBus.emit('MOVE_LAYER', { from: this.toIndex, to: this.fromIndex, skipRecord: true }); }
  redo(): void { EventBus.emit('MOVE_LAYER', { from: this.fromIndex, to: this.toIndex, skipRecord: true }); }
}

export class LayerPropertyAction implements IHistoryAction {
  readonly description: string;
  private readonly layerIndex: number;
  private readonly property: string;
  private readonly oldValue: unknown;
  private readonly newValue: unknown;

  constructor(layerIndex: number, property: string, oldValue: unknown, newValue: unknown) {
    this.description = `Change ${property}`;
    this.layerIndex = layerIndex;
    this.property = property;
    this.oldValue = oldValue;
    this.newValue = newValue;
  }

  undo(): void {
    const layer = layers[this.layerIndex] as any;
    if (layer) {
      layer[this.property] = this.oldValue;
      EventBus.emit('RENDER_LAYERS');
      EventBus.emit('UPDATE_LAYER_PANEL');
    }
  }

  redo(): void {
    const layer = layers[this.layerIndex] as any;
    if (layer) {
      layer[this.property] = this.newValue;
      EventBus.emit('RENDER_LAYERS');
      EventBus.emit('UPDATE_LAYER_PANEL');
    }
  }
}

export class VectorAddAction implements IHistoryAction {
  readonly description: string;
  private readonly layerIndex: number;
  private readonly shape: import('./types').Shape;

  constructor(layerIndex: number, shape: import('./types').Shape) {
    this.description = `Add ${shape.type.charAt(0).toUpperCase() + shape.type.slice(1)}`;
    this.layerIndex = layerIndex;
    this.shape = JSON.parse(JSON.stringify(shape)) as import('./types').Shape;
  }

  undo(): void {
    const layer = layers[this.layerIndex];
    if (layer?.shapes) {
      layer.shapes.pop();
      EventBus.emit('RENDER_VECTOR_LAYER', { layer });
      EventBus.emit('RENDER_LAYERS');
    }
  }

  redo(): void {
    const layer = layers[this.layerIndex];
    if (layer) {
      if (!layer.shapes) layer.shapes = [];
      layer.shapes.push(JSON.parse(JSON.stringify(this.shape)) as import('./types').Shape);
      EventBus.emit('RENDER_VECTOR_LAYER', { layer });
      EventBus.emit('RENDER_LAYERS');
    }
  }
}

export class VectorEditAction implements IHistoryAction {
  readonly description: string;
  private readonly layerIndex: number;
  private readonly shapeIndex: number;
  private readonly oldShape: import('./types').Shape;
  private readonly newShape: import('./types').Shape;

  constructor(
    layerIndex: number, shapeIndex: number,
    oldShape: import('./types').Shape, newShape: import('./types').Shape
  ) {
    this.description = `Edit ${newShape.type.charAt(0).toUpperCase() + newShape.type.slice(1)}`;
    this.layerIndex = layerIndex;
    this.shapeIndex = shapeIndex;
    this.oldShape = JSON.parse(JSON.stringify(oldShape)) as import('./types').Shape;
    this.newShape = JSON.parse(JSON.stringify(newShape)) as import('./types').Shape;
  }

  undo(): void {
    const layer = layers[this.layerIndex];
    if (layer?.shapes?.[this.shapeIndex]) {
      layer.shapes[this.shapeIndex] = JSON.parse(JSON.stringify(this.oldShape)) as import('./types').Shape;
      EventBus.emit('RENDER_VECTOR_LAYER', { layer });
      EventBus.emit('RENDER_LAYERS');
    }
  }

  redo(): void {
    const layer = layers[this.layerIndex];
    if (layer?.shapes?.[this.shapeIndex]) {
      layer.shapes[this.shapeIndex] = JSON.parse(JSON.stringify(this.newShape)) as import('./types').Shape;
      EventBus.emit('RENDER_VECTOR_LAYER', { layer });
      EventBus.emit('RENDER_LAYERS');
    }
  }
}

// ─── Singleton & Browser Globals ───────────────────────────

/**
 * Gets the history manager for the currently active document.
 */
export function getHistoryManager(): HistoryManager {
    const activeDoc = g.documents[g.activeDocumentIndex] as any;
    if (activeDoc && activeDoc.history) {
        return activeDoc.history;
    }
    
    // Fallback to a global one if no documents exist yet
    if (!(window as any)._fallbackHistory) {
      (window as any)._fallbackHistory = new HistoryManager(g.max_undo_steps);
    }
    return (window as any)._fallbackHistory;
}

// Global historyManager is now a dynamic property to ensure multi-doc support (#2064)
export const historyManager = new Proxy({} as HistoryManager, {
  get: (_target, prop) => {
    return (getHistoryManager() as any)[prop];
  },
  set: (_target, prop, value) => {
    (getHistoryManager() as any)[prop] = value;
    return true;
  }
});

declare global {
  interface Window {
    historyManager: HistoryManager;
    DrawAction: typeof DrawAction;
    LayerAddAction: typeof LayerAddAction;
    LayerDeleteAction: typeof LayerDeleteAction;
    LayerMoveAction: typeof LayerMoveAction;
    LayerPropertyAction: typeof LayerPropertyAction;
    VectorAddAction: typeof VectorAddAction;
    VectorEditAction: typeof VectorEditAction;
    renderLayers?: (liveCanvas?: HTMLCanvasElement | OffscreenCanvas) => void;
    updateLayerPanel?: () => void;
    moveLayer?: (from: number, to: number, skipRecord?: boolean) => void;
    renderVectorLayer?: (layer: LayerClass) => void;
  }
}

// Explicitly expose Action Classes to window for cross-package interoperability
if (typeof window !== 'undefined') {
    (window as any).HistoryManager = HistoryManager;
    (window as any).DrawAction = DrawAction;
    (window as any).LayerAddAction = LayerAddAction;
    (window as any).LayerDeleteAction = LayerDeleteAction;
    (window as any).LayerMoveAction = LayerMoveAction;
    (window as any).LayerPropertyAction = LayerPropertyAction;
    (window as any).VectorAddAction = VectorAddAction;
    (window as any).VectorEditAction = VectorEditAction;
}
